import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, unref as _unref, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, resolveDynamicComponent as _resolveDynamicComponent, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "distributor-map-container" }
const _hoisted_2 = ["onClick"]
const _hoisted_3 = { class: "name" }
const _hoisted_4 = { class: "country" }
const _hoisted_5 = {
  key: 0,
  class: "toggle-arrow"
}
const _hoisted_6 = {
  key: 0,
  class: "distributor-details"
}
const _hoisted_7 = { class: "detail-grid" }
const _hoisted_8 = ["href"]
const _hoisted_9 = {
  key: 1,
  class: "text"
}
const _hoisted_10 = {
  key: 0,
  class: "detail-item social-item"
}
const _hoisted_11 = { class: "social-icons" }
const _hoisted_12 = ["href", "title"]
const _hoisted_13 = { class: "social-link" }
const _hoisted_14 = { class: "social-name" }

import { ref, onMounted, onBeforeUnmount, computed, defineComponent, h, markRaw } from 'vue'
import * as echarts from 'echarts'
import { MapPin, Mail, Phone, Globe, Facebook, Instagram, Twitter, Youtube, Link as LinkIcon, ChevronUp, ChevronDown, ArrowRight } from 'lucide-vue-next'
import zimimodelLogo from '@/assets/dealer/zimimodel.png'

/**
 * 界面配置常量
 */
interface SocialMedia {
  platform: string
  name?: string
  url: string
}

interface Distributor {
  name: string
  country: string
  mapRegionName?: string
  logo?: string
  coords: [number, number]
  address?: string
  email?: string
  phone?: string
  website?: string
  socials?: SocialMedia[]
}

/**
 * 动态创建国内社交平台图标 (Badge 样式)
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'DistributorMap',
  props: {
  height: { type: Number, default: 600 }
},
  setup(__props) {

const CONFIG = {
  THEME: {
    PRIMARY: '#0052D9', // 品牌蓝
    ACTIVE: '#ff9900',  // 激活橙
    HOVER: '#e68a00',   // 悬停深橙
    MAP_BASE: '#e0e6ed',
    MAP_HOVER: '#b7cce4',
    BG: '#f5f7fa'
  },
  MAP: {
    MIN_ZOOM: 1.2,
    MAX_ZOOM: 5
  },
  WHEEL: {
    ITEM_WIDTH: 240,
    AUTO_CLOSE_DELAY: 2000
  }
}

// --- 类型定义 ---
const createBadgeIcon = (text: string, bgColor: string) => defineComponent({
  name: `Badge-${text}`,
  props: { size: { type: [Number, String], default: 24 } },
  render() {
    return h('div', {
      style: {
        width: '100%', height: '100%', borderRadius: '50%',
        backgroundColor: bgColor, color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: '600', fontSize: `${Number(this.size) * 0.5}px`, lineHeight: 1
      }
    }, text)
  }
})

// 社交平台组件映射
const SOCIAL_ICON_MAP: Record<string, any> = {
  'Facebook': Facebook,
  'Instagram': Instagram,
  'Twitter': Twitter,
  'Youtube': Youtube,
  'Bilibili': createBadgeIcon('B', '#00A1D6'),
  'WeChat': createBadgeIcon('W', '#09BB07'),
  'Douyin': createBadgeIcon('D', '#000000'),
  'Xiaohongshu': createBadgeIcon('R', '#FF2442'),
  'Weibo': createBadgeIcon('Wb', '#E6162D'),
  'Tieba': createBadgeIcon('T', '#2D8CF0')
}

const distributors: Distributor[] = [
  // Australia
  {
    name: 'Hobbies Australia',
    country: 'Australia',
    mapRegionName: 'Australia',
    coords: [151.75, -32.93],
    address: '34 Portside Cres, Newcastle, NSW, Australia',
    email: 'sales@hobbiesaustralia.com.au',
    phone: '0061-2-4961-0212',
    website: 'https://www.hobbiesaustralia.com.au/'
  },
  {
    name: 'BNA Model World',
    country: 'Australia',
    mapRegionName: 'Australia',
    coords: [144.96, -37.81],
    address: 'Melbourne, VIC, Australia',
    email: 'admin@bnamodelworld.com',
    website: 'https://www.bnamodelworld.com/'
  },

  // Bulgaria
  {
    name: 'HOBBYLAND Ltd',
    country: 'Bulgaria',
    mapRegionName: 'Bulgaria',
    coords: [23.32, 42.70],
    address: 'Southern region, Location "Byalata Vodenitsa 6" № 1, Hobbyland Shop'
  },

  // China Distributors
  { 
    name: 'ZiMiModel子米模型', 
    country: 'China', 
    mapRegionName: 'China', 
    logo: zimimodelLogo, 
    coords: [119.29, 26.07],
    address: '福建省 福州市 三坊七巷',
    email: 'zimimodel@hotmail.com',
    phone: '86-13348278844',
    website: 'https://zimimodel.com/',
    socials: [
      { platform: 'WeChat', name: '微信公众号 WeChat', url: 'https://mp.weixin.qq.com/s/eeW0a9OV760Tuq0eoUlFLQ' },
      { platform: 'Bilibili', name: '哔哩哔哩 Bilibili', url: 'https://space.bilibili.com/3546697114323504' },
      { platform: 'Douyin', name: '抖音 TikTok', url: 'https://v.douyin.com/VIp_3AU_0Yg/' },
      { platform: 'Xiaohongshu', name: '小红书 RED', url: 'https://www.xiaohongshu.com/user/profile/6093da2c000000000100b5b0' },
      { platform: 'Weibo', name: '微博 Weibo', url: 'https://weibo.com/u/7863161021' },
      { platform: 'Tieba', name: '贴吧 Tieba', url: 'https://tieba.baidu.com/home/main?id=tb.1.52384512.jSSskuulF5EjxJ1HnU5jvg' },
      { platform: 'Youtube', name: 'Youtube', url: 'https://www.youtube.com/@zimimodel' },
      { platform: 'Facebook', name: 'Facebook', url: 'https://www.facebook.com/p/ZimiModel-61567586481832/' }
    ]
  },
  {
    name: '上海3G模型',
    country: 'China',
    mapRegionName: 'China',
    coords: [121.47, 31.23],
    address: '上海市 嘉定区 江桥镇 高潮路695号 怡源商务楼A栋(办公楼)2-201室',
    phone: '021-64052380'
  },
  {
    name: '北京塑唐玩具',
    country: 'China',
    mapRegionName: 'China',
    coords: [116.40, 39.90],
    address: '北京市 朝阳区 东三环中路18号东环十八公寓3号楼首层(富力城对面)',
    phone: '400-116-6116'
  },
  {
    name: '广州文华模型',
    country: 'China',
    mapRegionName: 'China',
    coords: [113.26, 23.13],
    address: '广东省 广州市 越秀区 府学西街81号自编3栋10号',
    phone: '020-81854225'
  },
  {
    name: '巨嘴鸟模型',
    country: 'China',
    mapRegionName: 'China',
    coords: [114.41, 23.11],
    address: '广东省 惠州市',
    email: 'marketing@zimimodel.com',
    phone: '86-13348278844',
    website: 'https://m.tb.cn/h.hIxuZvkytayVfpc'
  },

  // France
  {
    name: 'SCIENTIFIC/MHD SARL',
    country: 'France',
    mapRegionName: 'France',
    coords: [3.53, 50.35],
    address: '272 Av. Henri Barbusse, 59770 Marly, France',
    email: 'contact@mhd.eu',
    phone: '0033-3-27-42-16-06',
    website: 'https://www.scientific-mhd.eu/'
  },

  // Germany
  {
    name: 'Modellbau-König GmbH',
    country: 'Germany',
    mapRegionName: 'Germany',
    coords: [8.63, 53.05],
    address: 'Nordenhamer Str. 177, 27751 Delmenhorst, Germany',
    email: 'support@modellbau-koenig.de',
    phone: '0049-4221-2890870',
    website: 'https://www.modellbau-koenig.de/'
  },
  {
    name: 'Glow2B Germany GmbH',
    country: 'Germany',
    mapRegionName: 'Germany',
    coords: [7.33, 51.20],
    address: 'Erlenbacher Str. 3, 42477 Radevormwald, Germany',
    email: 'mail@glow2b.de',
    phone: '0049-2195-927730',
    website: 'https://www.glow2b.com/en/'
  },

  // Hong Kong
  {
    name: '康田模型',
    country: 'Hong Kong',
    mapRegionName: 'China', 
    coords: [114.17, 22.30],
    address: '香港佐敦彌敦道345號',
    email: 'continental.skc@gmail.com',
    phone: '00852-2385-0623',
    website: 'https://www.facebook.com/p/Continental-Models-Toys-Company-%E5%BA%B7%E7%94%B0%E6%A8%A1%E5%9E%8B-100063903586291/?locale=zh_CN'
  },

  // Hungary
  {
    name: 'Makettshop',
    country: 'Hungary',
    mapRegionName: 'Hungary',
    coords: [21.85, 47.77],
    address: 'Balkány, Nefelejcs u. 2, 4233, Hungary',
    email: 'info@makettshop.hu',
    phone: '0036-703209946',
    website: 'https://www.makettshop.eu/'
  },

  // Japan
  {
    name: 'Hasegawa',
    country: 'Japan',
    mapRegionName: 'Japan',
    coords: [138.32, 34.86],
    address: '425-8711 静岡県 焼津市 八楠 3-1-2',
    email: 'export@hasegawa-model.biz',
    phone: '054-628-8241',
    website: 'http://www.hasegawa-model.co.jp/'
  },

  // Korea
  {
    name: 'HwajeonCorp',
    country: 'Korea',
    mapRegionName: 'South Korea',
    coords: [126.97, 37.56],
    address: '6th Fl, Shinsun Bldg, #535-127, Gasan Dong, Keumchun-ku, Seoul, Korea 153-023',
    email: 'ham@hwajeon.com',
    phone: '0082-2-704-7340'
  },

  // Malaysia
  {
    name: 'World Hobby Miniatures',
    country: 'Malaysia',
    mapRegionName: 'Malaysia',
    coords: [100.33, 5.41],
    address: '413A 1st Floor Jalan Burmah, Pulau Tikus, George Town, Malaysia',
    email: 'worldhobbyminiatures@gmail.com',
    phone: '0060-12-497-7712',
    website: 'https://www.facebook.com/whminiatures'
  },

  // Netherland
  {
    name: 'Model Engineering BV',
    country: 'Netherland',
    mapRegionName: 'Netherlands',
    coords: [5.15, 52.68],
    address: 'Perenmarkt 1, 1681 PG Zwaagdijk-Oost, Netherland',
    email: 'info@model-engineering.nl',
    phone: '0031-228-561-182',
    website: 'https://www.model-engineering.nl/'
  },

  // Poland
  {
    name: 'Netmarket Sp. z o.o.',
    country: 'Poland',
    mapRegionName: 'Poland',
    coords: [21.01, 52.23],
    address: 'ul. Czeresniowa 140/4, 02-456 Warszawa, POLAND',
    email: 'spaszczyk@netmarket.pl',
    phone: '0048-601226027'
  },

  // Singapore
  {
    name: 'Miniature Hobby International Pte Ltd',
    country: 'Singapore',
    mapRegionName: 'Singapore',
    coords: [103.88, 1.35],
    address: '8 Burn Road #14-16 Trivex, Singapore 369977',
    email: 'sylee@miniaturehobby.com',
    phone: '0065-6336-0862',
    website: 'http://miniaturehobby.com/'
  },

  // Switzerland
  {
    name: 'Replikamodellbau Wagner',
    country: 'Switzerland',
    mapRegionName: 'Switzerland',
    coords: [7.44, 46.95],
    address: 'Bottigenstrasse 25, 3018 Bern',
    email: 'info@replikamodellbau.ch',
    phone: '077-478-51-10',
    website: 'https://www.replikamodellbau.ch/'
  },

  // Taiwan
  {
    name: 'Gethobby',
    country: 'Taiwan',
    mapRegionName: 'Taiwan',
    coords: [121.56, 24.03],
    address: 'Taiwan',
  },
  {
    name: 'DREAM LINK CO., LTD',
    country: 'Taiwan',
    mapRegionName: 'Taiwan',
    coords: [121.54, 24.98],
    address: '231 新北市新店區復興路43號8樓',
    email: 'kimi@dreamlinktw.com',
    phone: '00886-2-2219-6299',
    website: 'https://www.dreamlinktw.com/home.jsp'
  },

  // Thailand
  {
    name: 'WINNER HOBBY LTD.',
    country: 'Thailand',
    mapRegionName: 'Thailand',
    coords: [100.52, 13.86],
    address: '36 Moo 2, Soi Rattanathibet 26, Rattanathibet Road, Bang Krasor Subdistrict, Mueang Nonthaburi District, Nonthaburi 11000, Thai',
    email: 'hobbynont@gmail.com',
    phone: '0081-8222669',
    website: 'https://www.winnerhobby.co.th/'
  },

  // Turkey
  {
    name: 'UGUR AKMAN-HOBBYTIME',
    country: 'Turkey',
    mapRegionName: 'Turkey',
    coords: [32.85, 39.93],
    address: 'uran Güneş Bulvarı Cezayir, Caddesi No:12/A Çankaya, Ankara 06550, Türkiye',
    email: 'siparis@hobbytime.com.tr',
    phone: '0090-312-438-40-31',
    website: 'https://www.hobbytime.com.tr/'
  },

  // Ukraine
  {
    name: 'Best Models',
    country: 'Ukraine',
    mapRegionName: 'Ukraine',
    coords: [30.72, 46.48],
    address: 'вул. Садова 16, Odessa, Ukraine',
    email: 'info@bestmodels.ua',
    phone: '00380-97-677-6877',
    website: 'https://bestmodels.ua/'
  },

  // United Kingdom
  {
    name: 'Amerang Ltd.',
    country: 'United Kingdom',
    mapRegionName: 'United Kingdom',
    coords: [-0.32, 50.83],
    address: 'Commerce Way, Lancing West Sussex BN15 8TA U.K.',
    email: 'enquiries@amerang.co.uk',
    phone: '0044-1903-765496',
    website: 'https://amerang.co.uk/'
  },

  // USA
  {
    name: "Andy's Hobby Headquarters",
    country: 'USA',
    mapRegionName: 'United States',
    coords: [-112.25, 33.45],
    address: '2150 S. 91st Avenue, Suite 105, Tolleson, Arizona, 85353',
    email: 'support@andyshhq.com',
    phone: '001-480-616-1011',
    website: 'https://andyshhq.com/'
  },
  {
    name: 'Sprue Brothers Models LLC',
    country: 'USA',
    mapRegionName: 'United States',
    coords: [-94.42, 39.24],
    address: '620 Haines, Liberty, MO, United States, Missouri',
    email: 'help@spruebrothers.com',
    phone: '001-816-759-8484',
    website: 'https://spruebrothers.com/'
  },
  {
    name: 'MMD',
    country: 'USA',
    mapRegionName: 'United States',
    coords: [-85.11, 34.91],
    address: '25 Southcross Ln, Ringgold, GA 30736',
    email: 'info@mmdmodels.com',
    phone: '001-800-527-0674',
    website: 'https://mmdmodels.com/'
  },

  // Vietnam
  {
    name: 'DMT Hobby',
    country: 'Vietnam',
    mapRegionName: 'Vietnam',
    coords: [106.63, 10.82],
    address: 'Ho Chi Minh City, Vietnam',
    email: 'dmthobby@gmail.com',
    phone: '0084-83-411-7755',
    website: 'https://dmt-kit.com/'
  }
]

const props = __props

// --- 状态管理：数据与 UI ---
const sortedDistributors = computed(() => distributors)
const currentDistributor = computed(() => sortedDistributors.value[currentIndex.value])

// 详情项配置 (用于 v-for 渲染)
const visibleDetails = computed(() => {
  if (!currentDistributor.value) return []
  const d = currentDistributor.value
  return [
    { label: 'Address', value: d.address, icon: MapPin },
    { label: 'Email', value: d.email, icon: Mail },
    { label: 'Phone', value: d.phone, icon: Phone },
    { label: 'Website', value: d.website, icon: Globe, isLink: true }
  ].filter(item => !!item.value)
})

// --- 状态管理：地图与交互 ---
const mapChart = ref<HTMLElement | null>(null)
let myChart: echarts.ECharts | null = null
const currentIndex = ref<number>(0)
const isDetailsVisible = ref<boolean>(true)
const isManualPinned = ref<boolean>(false) // "永久展开档"：手动操作锁定状态
const currentMapZoom = ref(CONFIG.MAP.MIN_ZOOM)

let autoCloseTimer: ReturnType<typeof setTimeout> | null = null

/**
 * 限制地图缩放范围
 */
const handleMapWrapperWheel = (e: WheelEvent) => {
  const isZoomingOut = e.deltaY > 0
  const isZoomingIn = e.deltaY < 0
  
  if ((isZoomingOut && currentMapZoom.value <= CONFIG.MAP.MIN_ZOOM + 0.001) ||
      (isZoomingIn && currentMapZoom.value >= CONFIG.MAP.MAX_ZOOM - 0.001)) {
    e.stopPropagation()
  }
}

/**
 * 切换详情面板可见性 (管理永久档位)
 */
const toggleDetails = () => {
  if (autoCloseTimer) {
    clearTimeout(autoCloseTimer)
    autoCloseTimer = null
  }
  isDetailsVisible.value = !isDetailsVisible.value
  isManualPinned.value = isDetailsVisible.value // 手动操作同步锁定状态
}

// --- 工具函数 ---
const formatUrl = (url: string) => url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')
const getSocialIcon = (platform: string) => SOCIAL_ICON_MAP[platform] || LinkIcon

/**
 * 初始化 ECharts 地图
 */
const initMap = async () => {
  if (!mapChart.value) return
  myChart = echarts.init(mapChart.value)
  myChart.showLoading()
  
  try {
    const response = await fetch('https://cdn.jsdelivr.net/npm/echarts@4.9.0/map/json/world.json')
    const worldJson = await response.json()
    myChart.hideLoading()
    echarts.registerMap('world', worldJson)

    // 因用户要求，在标题后新增“批量采购”入口，点击跳转到 /bulk 页面
    const option = {
      backgroundColor: CONFIG.THEME.BG,
      title: {
        text: '全球经销商分布 Global Dealer Distribution',
        // 因用户要求，优化跳转入口样式：改为蓝色、添加英文、使用更明确的跳转图标
        // subtext: '➜批量采购 Bulk Purchase',
        sublink: '/bulk',
        subtarget: '_self',
        left: 'center', top: 20,
        textStyle: { color: CONFIG.THEME.PRIMARY, fontSize: 16 },
        subtextStyle: { 
          color: CONFIG.THEME.PRIMARY, // 改为品牌蓝
          fontSize: 14, 
          fontWeight: 'bold',
          lineHeight: 20 // 增加行高，优化视觉间距
        },
        // 使用 triggerEvent 以便捕获标题点击（虽然 sublink 已经处理了跳转）
        triggerEvent: true
      },
      geo: {
        map: 'world', roam: true,
        scaleLimit: { min: CONFIG.MAP.MIN_ZOOM, max: CONFIG.MAP.MAX_ZOOM },
        zoom: CONFIG.MAP.MIN_ZOOM,
        label: {
          normal: { show: false },
          emphasis: { show: false }
        },
        itemStyle: {
          normal: { areaColor: CONFIG.THEME.MAP_BASE, borderColor: '#fff' },
          // 因用户要求（2026-03），禁用鼠标悬停时的国家区域蓝色高亮
          // emphasis: { areaColor: CONFIG.THEME.MAP_HOVER }
          emphasis: { areaColor: CONFIG.THEME.MAP_BASE }
        }
      }
    }
    
    myChart.setOption(option)
    myChart.on('click', (p: any) => {
      if (p.componentType === 'series') {
        const idx = sortedDistributors.value.findIndex(d => d.name === p.name)
        if (idx !== -1) focusDistributor(idx)
      }
    })
    myChart.on('georoam', () => {
      const opt = myChart?.getOption() as any
      if (opt?.geo?.[0]?.zoom) currentMapZoom.value = opt.geo[0].zoom
    })

    window.addEventListener('resize', handleResize)
    if (sortedDistributors.value.length > 0) selectDistributor(0)
    
  } catch (error: any) {
    console.error('Map loading failed', error)
    myChart.hideLoading()
  }
}

const handleResize = () => myChart?.resize()

/**
 * 核心切换逻辑：更新地图点并处理面板展示
 */
const selectDistributor = (index: number) => {
  if (autoCloseTimer) {
    clearTimeout(autoCloseTimer)
    autoCloseTimer = null
  }

  currentIndex.value = index
  isDetailsVisible.value = true

  // 自动模式下的定时收起逻辑
  if (!isManualPinned.value) {
    autoCloseTimer = setTimeout(() => {
      isDetailsVisible.value = false
      autoCloseTimer = null
    }, CONFIG.WHEEL.AUTO_CLOSE_DELAY)
  }

  const item = sortedDistributors.value[index]
  if (!myChart) return

  // 更新 ECharts 系列数据
  myChart.setOption({
    series: [{
      name: 'Distributors',
      type: 'scatter',
      coordinateSystem: 'geo',
      data: sortedDistributors.value.map((d, i) => ({
        name: d.name, value: [...d.coords],
        itemStyle: {
          color: i === index ? CONFIG.THEME.ACTIVE : CONFIG.THEME.PRIMARY
        },
        symbolSize: i === index ? 15 : 10,
        label: i === index ? {
          formatter: d.logo ? '{logo|}' : ' ',
          rich: { logo: { backgroundColor: { image: d.logo }, height: 30, width: 30, align: 'center' } },
          position: 'right', show: true, fontWeight: 'bold', fontSize: 14
        } : { show: false },
        z: i === index ? 10 : 1
      })),
      zlevel: 1
    }]
  })
  
  // 地图中心定位 (不再高亮整个国家区域)
  const currentOpt = myChart.getOption() as any
  myChart.setOption({
    geo: { center: item.coords, zoom: currentOpt?.geo?.[0]?.zoom || 3 }
  })
}

const focusDistributor = (idx: number) => {
  if (isClickSuppressed.value) return
  if (currentIndex.value === idx) {
    toggleDetails()
  } else {
    selectDistributor(idx)
  }
}

// --- 轮盘拖拽逻辑 ---
const dragOffset = ref(0)
const isDragging = ref(false)
const startX = ref(0)
const isClickSuppressed = ref(false)

const handleDragStart = (e: MouseEvent | TouchEvent) => {
  isDragging.value = true
  startX.value = 'touches' in e ? e.touches[0].clientX : e.clientX
  dragOffset.value = 0
  window.addEventListener('mousemove', handleDragMove)
  window.addEventListener('mouseup', handleDragEnd)
  window.addEventListener('touchmove', handleDragMove, { passive: false })
  window.addEventListener('touchend', handleDragEnd)
}

const handleDragMove = (e: MouseEvent | TouchEvent) => {
  if (!isDragging.value) return
  const currentX = 'touches' in e ? e.touches[0].clientX : e.clientX
  dragOffset.value = currentX - startX.value
  if (e.cancelable) e.preventDefault()
}

const handleDragEnd = () => {
  if (!isDragging.value) return
  window.removeEventListener('mousemove', handleDragMove)
  window.removeEventListener('mouseup', handleDragEnd)
  window.removeEventListener('touchmove', handleDragMove)
  window.removeEventListener('touchend', handleDragEnd)

  if (Math.abs(dragOffset.value) > 5) {
    isClickSuppressed.value = true
    setTimeout(() => { isClickSuppressed.value = false }, 50)
    if (Math.abs(dragOffset.value) > 50) {
      const direction = dragOffset.value < 0 ? 1 : -1
      const newIdx = Math.max(0, Math.min(currentIndex.value + direction, sortedDistributors.value.length - 1))
      if (newIdx !== currentIndex.value) selectDistributor(newIdx)
    }
  }
  isDragging.value = false
  dragOffset.value = 0
}

/**
 * 3D 轮盘样式计算 (位移、缩放、透明度)
 */
const getItemStyle = (index: number) => {
  const effectiveCurrentIndex = currentIndex.value - (dragOffset.value / CONFIG.WHEEL.ITEM_WIDTH)
  const diff = index - effectiveCurrentIndex
  const absDiff = Math.abs(diff)
  
  if (absDiff > 2.5) return { display: 'none' }

  const translateX = diff * CONFIG.WHEEL.ITEM_WIDTH
  const scale = 1 - (absDiff * 0.15)
  let opacity = 1
  if (absDiff >= 0.5 && absDiff < 1.5) opacity = 0.6
  if (absDiff >= 1.5) opacity = 0.3

  return {
    transform: `translateX(${translateX}px) scale(${scale})`,
    opacity,
    zIndex: 10 - Math.round(absDiff),
    transition: isDragging.value ? 'none' : 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
  }
}

// --- 生命周期 ---
onMounted(() => {
  initMap()
  mapChart.value?.addEventListener('wheel', handleMapWrapperWheel, { capture: true })
})

onBeforeUnmount(() => {
  if (autoCloseTimer) clearTimeout(autoCloseTimer)
  mapChart.value?.removeEventListener('wheel', handleMapWrapperWheel, { capture: true })
  window.removeEventListener('resize', handleResize)
  myChart?.dispose()
})



return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: "map-section",
      style: _normalizeStyle({ height: props.height + 'px' })
    }, [
      _createElementVNode("div", {
        class: "map-wrapper",
        ref_key: "mapChart",
        ref: mapChart
      }, null, 512),
      _createElementVNode("div", {
        class: _normalizeClass(["distributor-list-overlay", { 'is-expanded': isDetailsVisible.value }])
      }, [
        _createElementVNode("div", {
          class: "wheel-container",
          onMousedown: handleDragStart,
          onTouchstart: handleDragStart
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortedDistributors.value, (item, index) => {
            return (_openBlock(), _createElementBlock("div", {
              key: index,
              class: _normalizeClass(["distributor-item-3d", { active: currentIndex.value === index }]),
              style: _normalizeStyle(getItemStyle(index)),
              onClick: ($event: any) => (focusDistributor(index))
            }, [
              _createElementVNode("div", _hoisted_3, _toDisplayString(item.name), 1),
              _createElementVNode("div", _hoisted_4, _toDisplayString(item.country), 1),
              (currentIndex.value === index)
                ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                    (isDetailsVisible.value)
                      ? (_openBlock(), _createBlock(_unref(ChevronDown), {
                          key: 0,
                          size: 16
                        }))
                      : (_openBlock(), _createBlock(_unref(ChevronUp), {
                          key: 1,
                          size: 16
                        }))
                  ]))
                : _createCommentVNode("", true)
            ], 14, _hoisted_2))
          }), 128))
        ], 32)
      ], 2),
      _createVNode(_Transition, { name: "slide-fade" }, {
        default: _withCtx(() => [
          (currentDistributor.value && isDetailsVisible.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                _createElementVNode("div", _hoisted_7, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleDetails.value, (item) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: item.label,
                      class: "detail-item"
                    }, [
                      (_openBlock(), _createBlock(_resolveDynamicComponent(item.icon), {
                        class: "icon",
                        size: 20
                      })),
                      (item.isLink)
                        ? (_openBlock(), _createElementBlock("a", {
                            key: 0,
                            href: item.value,
                            target: "_blank",
                            rel: "noopener noreferrer",
                            class: "link"
                          }, _toDisplayString(formatUrl(item.value)), 9, _hoisted_8))
                        : (_openBlock(), _createElementBlock("div", _hoisted_9, _toDisplayString(item.value), 1))
                    ]))
                  }), 128)),
                  (currentDistributor.value.socials?.length)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                        _createElementVNode("div", _hoisted_11, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(currentDistributor.value.socials, (social, idx) => {
                            return (_openBlock(), _createElementBlock("a", {
                              key: idx,
                              href: social.url,
                              target: "_blank",
                              rel: "noopener",
                              class: "social-link-wrapper",
                              title: social.name || social.platform
                            }, [
                              _createElementVNode("div", _hoisted_13, [
                                (_openBlock(), _createBlock(_resolveDynamicComponent(getSocialIcon(social.platform)), { size: 20 }))
                              ]),
                              _createElementVNode("span", _hoisted_14, _toDisplayString(social.name || social.platform), 1)
                            ], 8, _hoisted_12))
                          }), 128))
                        ])
                      ]))
                    : _createCommentVNode("", true)
                ])
              ]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      })
    ], 4)
  ]))
}
}

})